import { RecordPager } from '@/components/admin/RecordPager';
import { actionLabel } from '@/components/platform/format';
import { accountError } from '@/lib/platform/accounts';
import { parseAuditPage, type AuditRow } from '@/lib/platform/audit';
import { listPath } from '@/lib/query/page';
import { useDebouncedSearch, useSectionParams } from '@/lib/query/table-params';
import { useTenantQuery } from '@/lib/query/useTenantQuery';

export function AuditPage({ embedded = false }: { embedded?: boolean }) {
  const table = useSectionParams('audit');
  const search = useDebouncedSearch('audit', table.search, table.write);
  const eventFilter = table.read('event');
  const actorFilter = table.read('actor');
  const slugFilter = table.read('slug');
  const auditQuery = useTenantQuery(
    ['platform-audit', table.page, table.search, eventFilter, actorFilter, slugFilter],
    listPath('/api/platform/audits', {
      page: table.page,
      perPage: 20,
      q: table.search,
      event: eventFilter,
      actor: actorFilter,
      slug: slugFilter,
    }),
    parseAuditPage,
  );
  const rows = auditQuery.data?.data ?? null;
  const total = auditQuery.data?.meta.total ?? 0;
  const error = auditQuery.isError && !auditQuery.data
    ? accountError(auditQuery.error, 'Could not load the audit.')
    : null;
  const filtersActive = table.search !== '' || eventFilter !== '' || actorFilter !== '' || slugFilter !== '';

  return (
    <section className="space-y-4">
      {!embedded && (
        <p className="max-w-3xl text-sm text-muted">
          Append-only platform trail: who acted, from which IP, which tenant or account was targeted, and the before/after
          state. Passwords, tokens, and authenticator secrets are never stored.
        </p>
      )}
      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          value={search.draft}
          onChange={(event) => search.onSearch(event.target.value)}
          placeholder="Search person, event, agency, or target"
          aria-label="Search the audit"
          className="w-full max-w-md rounded-xl border border-line bg-card px-3 py-2 text-sm text-ink outline-none focus:border-brand"
        />
        <input
          value={eventFilter}
          onChange={(event) => table.write({ event: event.target.value.trim() })}
          placeholder="Event"
          aria-label="Filter by event"
          className="w-full max-w-[12rem] rounded-xl border border-line bg-card px-3 py-2 text-sm text-ink outline-none focus:border-brand"
        />
        <input
          value={actorFilter}
          onChange={(event) => table.write({ actor: event.target.value.trim() })}
          placeholder="Actor id"
          aria-label="Filter by actor"
          className="w-full max-w-[12rem] rounded-xl border border-line bg-card px-3 py-2 text-sm text-ink outline-none focus:border-brand"
        />
      </div>
      {error && (
        <div className="rounded-2xl border border-danger/40 bg-card px-4 py-6 text-sm text-danger">
          <p>{error}</p>
          <button type="button" onClick={() => { void auditQuery.refetch(); }} className="mt-3 rounded-full border border-line px-3 py-1 text-xs font-semibold text-ink">Retry</button>
        </div>
      )}
      {auditQuery.isLoading && !error && (
        <div className="space-y-2" aria-hidden="true">
          {Array.from({ length: 6 }, (_, index) => <div key={index} className="h-10 animate-pulse rounded-xl bg-card" />)}
        </div>
      )}
      {rows?.length === 0 && !filtersActive && (
        <p className="rounded-2xl border border-line bg-card px-6 py-10 text-center text-sm text-muted">No platform changes are recorded yet.</p>
      )}
      {rows?.length === 0 && filtersActive && <p className="rounded-2xl border border-line bg-card px-6 py-10 text-center text-sm text-muted">No audit row matches that filter.</p>}
      {rows && rows.length > 0 && (
        <div className="overflow-x-auto rounded-2xl border border-line bg-card">
          <table className="min-w-full text-left text-sm">
            <thead className="sticky top-0 bg-card text-muted">
              <tr>
                <th className="type-table-header px-4 py-3">When</th>
                <th className="type-table-header px-4 py-3">Who</th>
                <th className="type-table-header px-4 py-3">Event</th>
                <th className="type-table-header px-4 py-3">Target</th>
                <th className="type-table-header px-4 py-3">IP</th>
                <th className="type-table-header px-4 py-3">Detail</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-t border-line align-top">
                  <td className="px-4 py-3 whitespace-nowrap text-ink">{auditTime(row.createdAt)}</td>
                  <td className="px-4 py-3 text-ink">
                    <p>{row.actorName}</p>
                    {row.actorId && <p className="font-mono text-[11px] text-muted">{row.actorId}</p>}
                  </td>
                  <td className="px-4 py-3 text-ink">
                    <p>{actionLabel(row.eventType)}</p>
                    <p className="font-mono text-[11px] text-muted">{row.eventType}</p>
                  </td>
                  <td className="px-4 py-3 text-ink">
                    {row.targetType || row.targetId ? (
                      <>
                        <p className="font-mono text-[11px] text-muted">{row.targetType ?? 'target'}</p>
                        <p className="break-all">{row.targetId ?? '—'}</p>
                      </>
                    ) : row.agencySlug ? (
                      <p>{row.agencySlug}</p>
                    ) : (
                      <p className="text-muted">Platform</p>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-[11px] text-muted">{row.actorIp ?? '—'}</td>
                  <td className="px-4 py-3 text-muted">
                    <p>{row.reason ?? '—'}</p>
                    {(row.before || row.after || row.metadata) && (
                      <details className="mt-2">
                        <summary className="cursor-pointer text-xs font-semibold text-ink">State</summary>
                        <div className="mt-2 space-y-2">
                          {row.before && <Snapshot label="Before" value={row.before} />}
                          {row.after && <Snapshot label="After" value={row.after} />}
                          {row.metadata && <Snapshot label="Context" value={row.metadata} />}
                        </div>
                      </details>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <RecordPager
        page={auditQuery.data?.meta.page ?? table.page}
        pages={Math.max(1, Math.ceil(total / (auditQuery.data?.meta.perPage || 20)))}
        total={rows === null ? -1 : total}
        onPage={(next) => table.write({ page: String(next) }, false)}
      />
    </section>
  );
}

function Snapshot({ label, value }: { label: string; value: Record<string, unknown> }) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted">{label}</p>
      <pre className="mt-1 max-h-48 overflow-auto rounded-xl bg-shell p-2 font-mono text-[11px] leading-4 text-ink">
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}

function auditTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}
